import React, { useEffect, useRef } from 'react';
import { AtSign, FileCode, FileText, GitBranch, Slash, Workflow } from 'lucide-react';
import type { MentionCandidate, MentionKind, SlashCommand } from '../../utils/composerMentions';

const KIND_ICON: Record<MentionKind, React.ComponentType<{ className?: string }>> = {
  program: FileCode,
  report: FileText,
  diagram: Workflow,
  version: GitBranch,
};

interface ComposerPickerProps {
  mode: 'mention' | 'command';
  mentions?: MentionCandidate[];
  commands?: SlashCommand[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onPickMention?: (candidate: MentionCandidate) => void;
  onPickCommand?: (command: SlashCommand) => void;
}

/**
 * Autocomplete above the composer for `@` artifact references and `/` commands.
 *
 * Keyboard-first: the arrow keys and Enter are handled by the composer itself so
 * the picker never steals focus from the textarea mid-sentence.
 */
export const ComposerPicker: React.FC<ComposerPickerProps> = ({
  mode,
  mentions = [],
  commands = [],
  activeIndex,
  onActiveIndexChange,
  onPickMention,
  onPickCommand,
}) => {
  const listRef = useRef<HTMLUListElement>(null);
  const itemCount = mode === 'mention' ? mentions.length : commands.length;

  useEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (itemCount === 0) return null;

  const rowClass = (index: number) => `flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
    index === activeIndex
      ? 'bg-cyan-50 text-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200'
      : 'text-zinc-600 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-white/[0.04]'
  }`;

  return (
    <div className="absolute bottom-full left-0 z-50 mb-2 w-full max-w-md overflow-hidden rounded-xl border border-[var(--color-border)] bg-white shadow-lg dark:border-white/10 dark:bg-[#161b22]">
      <div className="flex items-center gap-1.5 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px] text-zinc-400 dark:border-white/10 dark:text-zinc-500">
        {mode === 'mention' ? <AtSign className="h-3 w-3" /> : <Slash className="h-3 w-3" />}
        <span>{mode === 'mention' ? '引用本次会话的产物' : '快捷指令'}</span>
        <span className="ml-auto">↑↓ 选择 · Enter 确认 · Esc 关闭</span>
      </div>

      <ul ref={listRef} className="max-h-56 overflow-y-auto py-1" role="listbox">
        {mode === 'mention'
          ? mentions.map((candidate, index) => {
            const Icon = KIND_ICON[candidate.kind];
            return (
              <li key={candidate.token} role="option" aria-selected={index === activeIndex}>
                <button
                  type="button"
                  data-active={index === activeIndex}
                  onMouseEnter={() => onActiveIndexChange(index)}
                  onClick={() => onPickMention?.(candidate)}
                  className={rowClass(index)}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  <span className="truncate font-medium">{candidate.label}</span>
                  {candidate.detail ? (
                    <span className="ml-auto shrink-0 text-xs text-zinc-400 dark:text-zinc-500">{candidate.detail}</span>
                  ) : null}
                </button>
              </li>
            );
          })
          : commands.map((command, index) => (
            <li key={command.name} role="option" aria-selected={index === activeIndex}>
              <button
                type="button"
                data-active={index === activeIndex}
                onMouseEnter={() => onActiveIndexChange(index)}
                onClick={() => onPickCommand?.(command)}
                className={rowClass(index)}
              >
                <Slash className="h-3.5 w-3.5 shrink-0 opacity-60" />
                <span className="shrink-0 font-medium">/{command.name}</span>
                <span className="truncate text-xs text-zinc-400 dark:text-zinc-500">{command.summary}</span>
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
};
